<script setup>
// core
import {computed, getCurrentInstance, onMounted, ref} from "vue";
import {useI18n} from "vue-i18n";
import {useStore} from 'vuex'
import {useWindowSize} from "@vueuse/core";

// components
import SettingsHowItWorks from "@/components/Telephony/SettingsHowItWorks.vue";
import BaseSearchInput from "@/components/ui/BaseSearchInput.vue";
import ConnectedNumbersTable from "@/components/Telephony/ConnectedNumbersTable.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";
import ConnectedNumbersMobileTable from "@/components/Telephony/ConnectedNumbersMobileTable.vue";
import ConnectedNumbersNarrowTable from "@/components/Telephony/ConnectedNumbersNarrowTable.vue";
import BasePagination from '@/components/ui/BasePagination.vue'

const {width} = useWindowSize()
const {t: $t} = useI18n()
const {proxy} = getCurrentInstance()
const store = useStore()
const isConnectedTableLoading = ref(false)
const isConnectedSearchLoading = ref(false)
const connectedSearchQ = ref('')

// computed
const numbersList = computed(() => store.getters['telephony/connectedNumbersList'])
const numbersListMeta = computed(() => store.getters['telephony/connectedNumbersMeta'])

// methods
const onConnectedSearchApply = async () => {
    await loadConnectedNumbers(1, connectedSearchQ.value)
}

const loadConnectedNumbers = async (page = 1, search = undefined, append = false, perPage = 5) => {
    isConnectedTableLoading.value = true

    // the store keeps the list between visits; like the messenger tables, a fresh
    // load starts empty so the loader takes the rows' place instead of hanging under them
    if (!append) {
        isConnectedSearchLoading.value = true
        store.commit('telephony/SET_CONNECTED_NUMBERS_DATA', {data: null, append: false})
    }

    try {
        await store.dispatch('telephony/getConnectedNumbers', {
            query: {
                page: search ? 1 : page,
                per_page: perPage,
                number: search || undefined
            },
            moshaToast: proxy.$moshaToast,
            append
        })
    } finally {
        isConnectedTableLoading.value = false
        isConnectedSearchLoading.value = false
    }
}

const loadMoreConnectedNumbers = async () => {
    const currentPage = numbersListMeta.value.current_page || 1
    const totalPages = numbersListMeta.value.last_page || 1

    if (currentPage < totalPages && !isConnectedTableLoading.value) {
        await loadConnectedNumbers(currentPage + 1, connectedSearchQ.value, true)
    }
}

// modals
const openSettingsDialog = (item) => {
    store.commit('setDialog', 'NumberSettingsDialog')
    store.commit('openDialog', {
        name: 'NumberSettingsDialog',
        number: item.number,
        info: item,
    })
}

const openSelectSipDialog = (item) => {
    store.commit('setDialog', 'SelectSipDialog')
    store.commit('openDialog', {
        name: 'SelectSipDialog',
        number: item.number,
        info: item,
    })
}

// hooks

onMounted(async () => {
    await loadConnectedNumbers()
})
</script>

<template>
    <section class="settings">
        <h1 class="settings__title">{{ $t('telephony.settings.title') }}</h1>

        <SettingsHowItWorks/>

        <div class="settings__table">
            <div class="settings__tableHead">
                <h2 class="settings__tableTitle">{{ $t('telephony.connected_numbers.title') }}</h2>

                <BaseSearchInput
                    v-model="connectedSearchQ"
                    minLength="4"
                    numeric-only
                    :loading="isConnectedSearchLoading"
                    @submit-search="onConnectedSearchApply"
                />
            </div>

            <!-- seven columns need 1200px; 900–1199 gets six, narrower ones get cards -->
            <component
                :is="width >= 1200 ? ConnectedNumbersTable : ConnectedNumbersNarrowTable"
                v-if="width >= 900"
                :data="numbersList"
                :loading="isConnectedTableLoading"
                @load-more="loadMoreConnectedNumbers"
                @open-settings-dialog="openSettingsDialog"
                @open-select-sip-dialog="openSelectSipDialog"
            >
                <template #information_column>
                    <InfoDataTextRecurring
                        v-if="isConnectedTableLoading"
                        value="loading"
                        height="120px"
                    />

                    <InfoDataTextRecurring
                        v-if="!numbersList?.length && !isConnectedTableLoading"
                        value="noData"
                        height="120px"
                    />
                </template>
            </component>

            <template v-else>
                <InfoDataTextRecurring
                    v-if="isConnectedTableLoading"
                    value="loading"
                    height="120px"
                />
                <ConnectedNumbersMobileTable
                    :title="$t('telephony.connected_numbers.title')"
                    :data="numbersList"
                    @open-settings-dialog="openSettingsDialog"
                    @open-select-sip-dialog="openSelectSipDialog"
                />

                <InfoDataTextRecurring
                    v-if="!numbersList?.length && !isConnectedTableLoading"
                    value="noData"
                    height="120px"
                />

                <BasePagination
                    class=""
                    :lastPage="numbersListMeta.last_page"
                    :currentPage="numbersListMeta.current_page"
                    @onChangePage="(numberPage) => loadConnectedNumbers(numberPage, connectedSearchQ)"
                />
            </template>
        </div>
    </section>
</template>

<style scoped lang="scss">
.settings {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 30px;

    &__title {
        color: #404040;
        font-family: Gilroy;
        font-size: 24px;
        font-weight: 500;
        text-transform: uppercase;
    }

    &__table {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__tableHead {
        display: flex;
        align-items: center;
        gap: 8px;

        @media (max-width: 480px) {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    // .selected-numbers-title of HeaderTableBlock
    &__tableTitle {
        position: relative;
        flex: 1;
        font-family: "Gilroy";
        font-size: 24px;
        line-height: 28px;
        font-weight: 500;
        text-transform: uppercase;

        @media (max-width: 740px) {
            font-size: 20px;
        }
    }

}
</style>
